<svelte:options runes={false} />
<script>
  // Real-time "next bar" countdown for a Running strategy. Candle bars are aligned to the UTC epoch grid
  // (the engine's OhlcTime.AlignDown: slot = time − time % frequency), so the next bar CLOSES at
  // ceil(now / frequency) × frequency — that is the moment the loop can pick up a new closed candle (it then
  // polls within ~30s, plus exchange publish lag). Display contract (retail-investor friendly):
  //   < 24h away  → a live hh:mm:ss countdown ticking every second;
  //   ≥ 24h away  → "Tomorrow at HH:MM" when it lands tomorrow (local), else the full local date & time.
  // Renders nothing unless the strategy is Running (a paused/stopped strategy has no next bar).
  import { onDestroy } from 'svelte';

  export let frequencySeconds = 0;
  export let status = null;

  let nowMs = Date.now();
  let timer = null;

  $: isRunning = String(status) === 'Running';

  // Tick only while visible + Running (1s resolution; trivial work per tick).
  $: if (isRunning && frequencySeconds > 0 && !timer) {
    timer = setInterval(() => { nowMs = Date.now(); }, 1000);
  } else if ((!isRunning || frequencySeconds <= 0) && timer) {
    clearInterval(timer);
    timer = null;
  }
  onDestroy(() => { if (timer) clearInterval(timer); });

  // Next bar boundary (UTC epoch grid). Recomputed each tick; rolls over automatically when it passes.
  $: stepMs = frequencySeconds * 1000;
  $: targetMs = stepMs > 0 ? (Math.floor(nowMs / stepMs) + 1) * stepMs : 0;
  $: remainingMs = Math.max(0, targetMs - nowMs);
  $: isCountdown = remainingMs < 24 * 3600 * 1000;

  function pad(n) { return String(n).padStart(2, '0'); }

  $: countdownLabel = (() => {
    const totalSec = Math.round(remainingMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  })();

  $: absoluteLabel = (() => {
    const target = new Date(targetMs);
    const now = new Date(nowMs);
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const time = target.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (sameDay(target, tomorrow)) return `Tomorrow at ${time}`;
    return `${target.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} at ${time}`;
  })();
</script>

{#if isRunning && frequencySeconds > 0}
  <span class="at-next-bar" title="When the next candle bar closes and the strategy evaluates again">
    <span class="at-next-bar-label">Next bar</span>
    {#if isCountdown}
      <span class="at-next-bar-value at-next-bar-ticking" aria-live="off">{countdownLabel}</span>
    {:else}
      <span class="at-next-bar-value">{absoluteLabel}</span>
    {/if}
  </span>
{/if}

<style>
  .at-next-bar { display: inline-flex; align-items: baseline; gap: 0.35rem; }
  .at-next-bar-label {
    font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--text-muted, #8a8b8d);
  }
  .at-next-bar-value {
    font-size: 0.85rem; font-weight: 700; color: var(--text-primary, #e8e9ea);
    font-variant-numeric: tabular-nums;
  }
  .at-next-bar-ticking { color: var(--accent, #3c78f0); }
</style>
